export type ElementDimensions = {
  width: number;
  height: number;
};

type DOMRectLike = Pick<DOMRectReadOnly, "width" | "height">;

type FrameScheduler = (callback: FrameRequestCallback) => number;
type FrameCanceller = (handle: number) => void;

export function createBufferedSizeReporter(
  apply: (size: ElementDimensions) => void,
  options?: {
    requestAnimationFrame?: FrameScheduler;
    cancelAnimationFrame?: FrameCanceller;
  },
): {
  report(rect: DOMRectLike): void;
  cancel(): void;
} {
  let pendingSize: ElementDimensions | null = null;
  let frameHandle: number | null = null;

  const hasWindow = typeof window !== "undefined";
  const raf =
    options && "requestAnimationFrame" in options
      ? options.requestAnimationFrame
      : hasWindow
        ? window.requestAnimationFrame
        : undefined;
  const caf =
    options && "cancelAnimationFrame" in options
      ? options.cancelAnimationFrame
      : hasWindow
        ? window.cancelAnimationFrame
        : undefined;

  const commit = () => {
    frameHandle = null;
    if (!pendingSize) {
      return;
    }
    const nextSize = pendingSize;
    pendingSize = null;
    apply(nextSize);
  };

  const normaliseRect = (rect: DOMRectLike): ElementDimensions => ({
    width: Math.max(0, Math.floor(rect.width ?? 0)),
    height: Math.max(0, Math.floor(rect.height ?? 0)),
  });

  return {
    report(rect) {
      pendingSize = normaliseRect(rect);

      if (typeof raf !== "function") {
        commit();
        return;
      }

      if (frameHandle !== null) {
        return;
      }

      frameHandle = raf(() => {
        commit();
      });
    },
    cancel() {
      if (frameHandle !== null && typeof caf === "function") {
        caf(frameHandle);
      }
      frameHandle = null;
      pendingSize = null;
    },
  };
}
